import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { getSafeUser } from "@/lib/auth-utils";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { folderSchema } from "@/lib/validations";
import { logger } from "@/lib/logger";
import * as RemixIcon from "@remixicon/react";
import { IconEmojiPicker, FOLDER_COLORS, isEmojiIcon, getIconComponent } from "@/components/ui/icon-emoji-picker";

interface QuickCreateFolderDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onFolderCreated?: (folderId: string) => void;
  parentFolderId?: string;
}



interface FolderWithDepth {
  id: string;
  name: string;
  depth: number;
  parent_id: string | null;
}

interface Folder {
  id: string;
  name: string;
  parent_id: string | null;
}

export default function QuickCreateFolderDialog({
  open,
  onOpenChange,
  onFolderCreated,
  parentFolderId,
}: QuickCreateFolderDialogProps) {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [color, setColor] = useState(FOLDER_COLORS[0]);
  const [icon, setIcon] = useState<string>('folder');
  const [selectedParentId, setSelectedParentId] = useState<string | undefined>(parentFolderId);
  const [customColor, setCustomColor] = useState("");
  const [saving, setSaving] = useState(false);
  const [foldersWithDepth, setFoldersWithDepth] = useState<FolderWithDepth[]>([]);
  const [loadingFolders, setLoadingFolders] = useState(false);

  // Load folders for parent selection
  useEffect(() => {
    if (open) {
      loadFolders();
    }
  }, [open]);

  // Update selected parent if prop changes
  useEffect(() => {
    setSelectedParentId(parentFolderId);
  }, [parentFolderId]);

  const loadFolders = async () => {
    setLoadingFolders(true);
    try {
      const { user, error: authError } = await getSafeUser();
      if (authError || !user) return;

      const { data, error } = await supabase
        .from("folders")
        .select("id, name, parent_id")
        .eq("user_id", user.id)
        .order("name");

      if (error) throw error;

      // Compute depth for each folder
      const foldersMap = new Map<string, Folder>(
        (data || []).map(f => [f.id, f])
      );

      const computeDepth = (folderId: string, visited = new Set<string>()): number => {
        if (visited.has(folderId)) return 0; // Prevent infinite loops
        visited.add(folderId);

        const folder = foldersMap.get(folderId);
        if (!folder || !folder.parent_id) return 0;
        return 1 + computeDepth(folder.parent_id, visited);
      };

      const foldersWithDepth: FolderWithDepth[] = (data || []).map(f => ({
        ...f,
        depth: computeDepth(f.id),
      }));

      setFoldersWithDepth(foldersWithDepth);
    } catch (error) {
      logger.error("Error loading folders", error);
    } finally {
      setLoadingFolders(false);
    }
  };

  const handleCreate = async () => {
    // Validate input
    const validation = folderSchema.safeParse({
      name: name.trim(),
      description: description.trim() || undefined
    });

    if (!validation.success) {
      toast.error(validation.error.errors[0]?.message || "Invalid folder name");
      return;
    }

    setSaving(true);
    try {
      const { user, error: authError } = await getSafeUser();
      if (authError || !user) {
        toast.error("You must be logged in to create folders");
        return;
      }

      // Check if parent folder would violate depth constraint
      if (selectedParentId) {
        const parentFolder = foldersWithDepth.find(f => f.id === selectedParentId);
        if (parentFolder && parentFolder.depth >= 2) {
          toast.error("Cannot create folder: Maximum folder depth is 3 levels");
          return;
        }
      }

      // Check for duplicate name within same parent
      let query = supabase
        .from("folders")
        .select("id")
        .eq("user_id", user.id)
        .eq("name", validation.data.name);

      // Use .is() for null comparison, .eq() for actual values
      if (selectedParentId) {
        query = query.eq("parent_id", selectedParentId);
      } else {
        query = query.is("parent_id", null);
      }

      const { data: existingFolder, error: checkError } = await query.maybeSingle();

      if (checkError) throw checkError;

      if (existingFolder) {
        const location = selectedParentId
          ? `in folder "${foldersWithDepth.find(f => f.id === selectedParentId)?.name}"`
          : "at root level";
        toast.error(`A folder named "${validation.data.name}" already exists ${location}`);
        return;
      }

      // Create folder
      const finalColor = customColor || color;
      const { data, error } = await supabase
        .from("folders")
        .insert({
          name: validation.data.name,
          description: validation.data.description,
          user_id: user.id,
          parent_id: selectedParentId || null,
          icon,
          color: finalColor,
          position: 0,
        })
        .select()
        .single();

      if (error) throw error;

      toast.success("Folder created successfully");
      onFolderCreated?.(data.id);
      onOpenChange(false);
      resetForm();
    } catch (error) {
      logger.error("Error creating folder", error);
      toast.error("Failed to create folder");
    } finally {
      setSaving(false);
    }
  };

  const resetForm = () => {
    setName("");
    setDescription("");
    setColor(FOLDER_COLORS[0]);
    setIcon('folder');
    setCustomColor("");
    setSelectedParentId(parentFolderId);
  };

  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) {
      resetForm();
    }
    onOpenChange(newOpen);
  };

  // Use shared utilities from icon-emoji-picker
  const isEmoji = isEmojiIcon(icon);
  const IconComponent = getIconComponent(icon);

  // Filter folders to only show those that can be parents (depth < 2)
  const availableParentFolders = foldersWithDepth.filter(f => f.depth < 2);

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Create New Folder</DialogTitle>
          <DialogDescription>
            Add a new folder to organize your meeting calls
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Name */}
          <div className="space-y-2">
            <Label htmlFor="folder-name">Folder Name *</Label>
            <Input
              id="folder-name"
              placeholder="e.g., Client Meetings, Team Calls"
              value={name}
              onChange={(e) => setName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !saving) {
                  handleCreate();
                }
              }}
              autoFocus
            />
          </div>

          {/* Description */}
          <div className="space-y-2">
            <Label htmlFor="folder-description">Description (optional)</Label>
            <Input
              id="folder-description"
              placeholder="Brief description of this folder"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </div>

          {/* Parent Folder */}
          <div className="space-y-2">
            <Label htmlFor="parent-folder">Parent Folder (optional)</Label>
            <Select
              value={selectedParentId || "none"}
              onValueChange={(value) => setSelectedParentId(value === "none" ? undefined : value)}
              disabled={loadingFolders}
            >
              <SelectTrigger id="parent-folder">
                <SelectValue placeholder="Select parent folder" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">None (Root Level)</SelectItem>
                {availableParentFolders.map((folder) => (
                  <SelectItem key={folder.id} value={folder.id}>
                    {"  ".repeat(folder.depth)}{folder.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {selectedParentId && foldersWithDepth.find(f => f.id === selectedParentId)?.depth === 2 && (
              <p className="text-xs text-destructive">
                This folder is already at maximum depth
              </p>
            )}
          </div>

          {/* Color Picker */}
          <div className="space-y-2">
            <Label>Color</Label>
            <div className="flex gap-2 flex-wrap items-center">
              {FOLDER_COLORS.map((colorValue) => (
                <button
                  key={colorValue}
                  type="button"
                  onClick={() => {
                    setColor(colorValue);
                    setCustomColor("");
                  }}
                  className={`w-8 h-8 rounded-md border-2 transition-all ${
                    color === colorValue && !customColor
                      ? 'border-cb-ink scale-110'
                      : 'border-cb-border hover:scale-105'
                  }`}
                  style={{ backgroundColor: colorValue }}
                  title={colorValue}
                />
              ))}
              <div className="flex items-center gap-2 ml-2">
                <Label htmlFor="custom-color" className="text-xs">
                  Custom:
                </Label>
                <input
                  id="custom-color"
                  type="color"
                  value={customColor || color}
                  onChange={(e) => setCustomColor(e.target.value)}
                  className="w-8 h-8 rounded border border-cb-border cursor-pointer"
                />
              </div>
            </div>
          </div>

          {/* Icon/Emoji Picker */}
          <div className="space-y-2">
            <Label>Icon or Emoji</Label>
            <IconEmojiPicker
              value={icon}
              onChange={setIcon}
              color={customColor || color}
            />
          </div>

          {/* Preview */}
          <div className="space-y-2">
            <Label>Preview</Label>
            <div className="flex items-center gap-2 p-3 rounded-md border border-cb-border bg-cb-card">
              {isEmoji ? (
                <span className="text-xl">{icon}</span>
              ) : IconComponent ? (
                <IconComponent
                  className="h-5 w-5"
                  style={{ color: customColor || color }}
                />
              ) : (
                <RemixIcon.RiFolderLine
                  className="h-5 w-5"
                  style={{ color: customColor || color }}
                />
              )}
              <span className="font-medium">
                {name || "Folder Name"}
              </span>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="hollow" onClick={() => handleOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleCreate} disabled={saving || !name.trim()}>
            {saving ? "Creating..." : "Create Folder"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
